import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Leaf, Thermometer, Wind, Droplets, Snowflake, Calendar, MapPin, Users,
  ArrowUpRight, ExternalLink, Recycle, Waves, Wheat, Building2, Factory,
  Zap, Handshake, GraduationCap, Link2, HeartPulse, ArrowDown,
  Bird, Fish, Flame, Bug, Dna, Microscope,
} from 'lucide-react';

const IMG_HERO    = 'https://images.unsplash.com/photo-1466611653911-95081537e5b7?auto=format&fit=crop&w=1800&q=80'; // rüzgar türbinleri, gün batımı
const IMG_PROBLEM  = 'https://images.unsplash.com/photo-1611273426858-450d8e3c9fce?auto=format&fit=crop&w=1200&q=80'; // endüstriyel emisyon
const IMG_SOLUTION = 'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1200&q=80'; // güneş panelleri
const IMG_COP31    = 'https://images.unsplash.com/photo-1614730321146-b6fa6a46bcb4?auto=format&fit=crop&w=1400&q=80'; // uzaydan dünya

const ClimateSection = () => {
  const { i18n } = useTranslation();
  const isEn = i18n.language === 'en';

  const stats = [
    {
      icon: Thermometer,
      value: '+1,2°C',
      label: isEn ? 'Global Temperature Rise' : 'Küresel Sıcaklık Artışı',
      desc: isEn
        ? 'Above pre-industrial levels, per the IPCC Sixth Assessment Report.'
        : 'Sanayi öncesi döneme kıyasla; IPCC Altıncı Değerlendirme Raporu verileri.',
      color: '#ea580c',
    },
    {
      icon: Wind,
      value: '420 ppm+',
      label: isEn ? 'Atmospheric CO₂' : 'Atmosferik CO₂ Yoğunluğu',
      desc: isEn
        ? 'The highest concentration recorded in the last 800,000 years.'
        : 'Son 800 bin yılın en yüksek atmosferik karbondioksit seviyesi.',
      color: '#0f766e',
    },
    {
      icon: Droplets,
      value: '+20 cm',
      label: isEn ? 'Sea Level Rise' : 'Deniz Seviyesi Yükselişi',
      desc: isEn
        ? 'Global average rise in sea level since 1900.'
        : '1900\'den bu yana küresel ortalama deniz seviyesindeki artış.',
      color: '#0369a1',
    },
    {
      icon: Snowflake,
      value: '%13',
      label: isEn ? 'Arctic Sea Ice Decline' : 'Kuzey Kutbu Buzul Kaybı',
      desc: isEn
        ? 'Decade-over-decade decline rate of Arctic sea ice.'
        : 'Kuzey Kutbu deniz buzulunun on yıllık ortalama azalma oranı.',
      color: '#0891b2',
    },
  ];

  const themes = [
    { icon: Recycle,       tr: 'Sıfır Atık',              en: 'Zero Waste',                    color: '#059669' },
    { icon: Waves,         tr: 'Okyanuslar ve Denizler',  en: 'Oceans & Seas',                 color: '#0284c7' },
    { icon: Wheat,         tr: 'Gıda Güvenliği',          en: 'Food Security',                 color: '#d97706' },
    { icon: Building2,     tr: 'İklim Dirençli Şehirler', en: 'Climate-Resilient Cities',       color: '#7c3aed' },
    { icon: Factory,       tr: 'Yeşil Sanayileşme',       en: 'Green Industrialisation',        color: '#0d9488' },
    { icon: Zap,           tr: 'Temiz Enerji Dönüşümü',   en: 'Clean Energy Transition',         color: '#f59e0b' },
    { icon: Handshake,     tr: 'Rio Sinerjisi',           en: 'Rio Synergy',                    color: '#4f46e5' },
    { icon: GraduationCap, tr: 'Gençlik ve Eğitim',       en: 'Youth & Education',               color: '#db2777' },
    { icon: Link2,         tr: 'İklim Uygulama Köprüsü',  en: 'Climate Implementation Bridge',   color: '#0891b2' },
    { icon: HeartPulse,    tr: 'Sağlık Sistemleri',       en: 'Health Systems',                 color: '#e11d48' },
  ];

  const researchAreas = [
    {
      icon: Bird,
      color: '#0d9488',
      title: {
        tr: 'Biyocoğrafya, Ekolojik Niş Modellemesi ve Biyoçeşitlilik',
        en: 'Biogeography, Ecological Niche Modelling & Biodiversity',
      },
      items: [
        {
          name: 'Prof. Dr. Utku Perktaş',
          lab: isEn ? 'Biogeography Research Laboratory' : 'Biyocoğrafya Araştırma Laboratuvarı',
          desc: isEn
            ? "Studies the effects of climate change on species' genetic diversity, geographic ranges and extinction risk. A study on the Australian songbird Malurus melanocephalus modelled the loss of most of the species' genetic diversity and subspecies-level populations under future warming scenarios, published in the Biological Journal of the Linnean Society."
            : 'İklim değişikliğinin türlerin genetik çeşitliliği, coğrafi dağılım alanları ve yok olma riskleri üzerindeki etkilerini incelemektedir. Avustralya ötücü kuşlarından Malurus melanocephalus üzerinde yürütülen çalışmada, küresel ısınma senaryolarına bağlı olarak türün genetik çeşitliliğinin büyük kısmının ve alt tür popülasyonlarının kaybolacağı modellenerek Biological Journal of the Linnean Society dergisinde yayımlanmıştır.',
        },
        {
          name: 'Doç. Dr. Mehmet Kürşat Şahin',
          desc: isEn
            ? 'Leads a joint TÜBİTAK (Türkiye)–Shota Rustaveli (Georgia) project on climate-change adaptation and ecological niche shifts in Caucasian rock lizards, using thermoregulation, microbiome, diet and whole-genome analyses to study effects on reptile physiology and niche shift.'
            : 'Kafkas kaya kertenkeleleri üzerinden iklim değişikliğine uyum ve ekolojik niş kaymalarını inceleyen TÜBİTAK (Türkiye) – Shota Rustaveli (Gürcistan) ortak projesinin yürütücüsüdür. Termoregülasyon, mikrobiyom, diyet ve tüm genom analizleri kullanarak iklim değişikliğinin sürüngenlerin fizyolojisi ve niş kayması üzerindeki etkilerini araştırmaktadır.',
        },
        {
          name: isEn
            ? 'Prof. Dr. Mahmut Kabalak, Dr. Çağaşan Karacaoğlu & Ecology/Systematics Researchers'
            : 'Prof. Dr. Mahmut Kabalak, Dr. Çağaşan Karacaoğlu ve Biyoloji Ekoloji/Sistematik Araştırmacıları',
          desc: isEn
            ? "Use Ecological Niche Modelling (ENM) to study how climate change will affect the future distribution of Anatolian insect and arthropod species (family Elateridae, Cantharis rustica, etc.); the study on Cantharis rustica received an award at an international climate congress."
            : "Ekolojik Niş Modellemesi (ENM) yöntemleri kullanarak iklim değişikliğinin Anadolu'daki böcek ve eklem bacaklı türlerinin (Elateridae familyası, Cantharis rustica vb.) gelecekteki potansiyel yayılımlarına etkilerini araştırmaktadır. İklim değişikliğinin Cantharis rustica üzerindeki olası etkileri çalışması uluslararası iklim kongrelerinde ödüllendirilmiştir.",
        },
      ],
    },
    {
      icon: Fish,
      color: '#0369a1',
      title: { tr: 'Hidrobiyoloji ve İstilacı Tür Risk Analizleri', en: 'Hydrobiology & Invasive Species Risk Analyses' },
      items: [
        {
          name: 'Dr. Baran Yoğurtçuoğlu',
          lab: isEn ? 'Department of Hydrobiology' : 'Hidrobiyoloji Anabilim Dalı',
          desc: isEn
            ? "Studies the pressures of climate change and global warming on freshwater ecosystems, and has published large-scale risk analyses assessing the growing invasive potential of exotic/aquarium fish species entering Türkiye's freshwaters under rising-temperature scenarios."
            : 'İklim değişikliği ve küresel ısınmanın tatlı su ekosistemleri üzerindeki baskılarını incelemektedir. Türkiye tatlı sularına giren egzotik/akvaryum balığı türlerinin yükselen sıcaklık senaryoları altında istilacı potansiyellerinin artışını değerlendiren geniş ölçekli risk analizleri ve uluslararası yayınlar yayımlamıştır.',
        },
      ],
    },
    {
      icon: Flame,
      color: '#ea580c',
      title: { tr: 'Bitki Ekolojisi ve Yangın Rejimleri', en: 'Plant Ecology & Fire Regimes' },
      items: [
        {
          name: 'Prof. Dr. Çağatay Tavşanoğlu',
          lab: isEn ? 'Fire & Plant Ecology' : 'Yangın ve Bitki Ekolojisi',
          desc: isEn
            ? 'Models how rising temperatures and drought are altering fire regimes in the Mediterranean climate basin, how plant communities respond to climatic change, and the historical-to-present climatic biogeography of oak species (Quercus robur).'
            : 'Akdeniz iklim havzasında sıcaklık artışı ve kuraklıkla değişen yangın rejimlerini, bitki topluluklarının iklimsel değişimlere yanıtını ve meşe türlerinin (Quercus robur) geçmişten günümüze iklimsel biyocoğrafyasını modellemektedir.',
        },
      ],
    },
    {
      icon: Bug,
      color: '#7c3aed',
      title: { tr: 'Vektör Biyolojisi ve İklim-Hastalık İlişkileri', en: 'Vector Biology & Climate–Disease Relationships' },
      items: [
        {
          name: isEn ? 'M. Nihat Şişli Ecological Sciences Research Laboratory (EBAL)' : 'M. Nihat Şişli Ekolojik Bilimler Araştırma Laboratuvarı (EBAL)',
          desc: isEn
            ? 'Studies the effects of climate change on the population dynamics, geographic distribution and disease-transmission patterns of mosquitoes (Culicidae) and sand flies (Psychodidae). Under Horizon Europe and TÜBİTAK projects, predictive distribution maps of disease-carrying vectors are produced for different climate scenarios.'
            : 'İklim değişikliğinin sivrisinek (Culicidae) ve tatarcık (Psychodidae) popülasyonlarının dinamiklerine, coğrafi yayılımlarına ve bulaştırdıkları hastalık modellerine etkileri üzerinde çalışılmaktadır. AB Ufuk Avrupa (Horizon) ve TÜBİTAK projeleri kapsamında, iklim senaryolarına göre hastalık taşıyıcı vektörlerin tahmini dağılım haritaları üretilmektedir.',
        },
      ],
    },
    {
      icon: Dna,
      color: '#059669',
      title: { tr: 'Genetik Adaptasyon ve Koruma Biyolojisi', en: 'Genetic Adaptation & Conservation Biology' },
      items: [
        {
          name: isEn ? 'Climate Experiments with Model Organisms' : 'Model Organizmalar ile İklim Deneyleri',
          desc: isEn
            ? 'To help protect endemic species at high risk under future climate scenarios, genetic and physiological adaptation mechanisms to high-temperature/stress conditions are studied using model organisms such as Drosophila melanogaster (fruit fly).'
            : 'Gelecek iklim senaryolarına karşı riski yüksek endemik canlı türlerinin korunmasına altlık oluşturmak amacıyla, Drosophila melanogaster (sirke sineği) gibi model organizmalar üzerinden yüksek sıcaklık/stres koşullarına genetik ve fizyolojik adaptasyon mekanizmaları araştırılmaktadır.',
        },
      ],
    },
  ];

  return (
    <section className="relative overflow-hidden py-20 md:py-24" style={{ background: 'linear-gradient(180deg, #f2faf6 0%, #ffffff 55%)' }}>
      {/* Dekoratif arka plan lekeleri */}
      <div className="absolute -top-32 -left-32 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)' }} />
      <div className="absolute top-1/3 -right-32 w-96 h-96 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(14,116,144,0.1) 0%, transparent 70%)' }} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">

        {/* ══════════ HERO: İklim Değişikliği ══════════ */}
        <motion.div
          className="relative rounded-3xl overflow-hidden mb-6"
          style={{ minHeight: '460px', boxShadow: '0 30px 80px rgba(6,60,50,0.25)' }}
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <img src={IMG_HERO} alt={isEn ? 'Wind turbines at sunset' : 'Gün batımında rüzgar türbinleri'} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(4,20,16,0.55) 0%, rgba(4,20,16,0.45) 45%, rgba(3,15,12,0.94) 100%)' }} />

          <div className="relative z-10 flex flex-col items-center justify-end h-full text-center px-6 md:px-16 py-12" style={{ minHeight: '460px' }}>
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.24em] mb-5" style={{
              background: 'rgba(16,185,129,0.18)',
              border: '1px solid rgba(110,231,183,0.4)',
              color: '#6ee7b7',
              backdropFilter: 'blur(8px)',
            }}>
              <Leaf className="w-3 h-3" />
              {isEn ? 'Science & Sustainability' : 'Bilim ve Sürdürülebilirlik'}
            </span>

            <h2 className="font-black text-white leading-tight mb-4" style={{ fontSize: 'clamp(1.9rem, 3.6vw, 2.9rem)', textShadow: '0 4px 24px rgba(0,0,0,0.4)' }}>
              {isEn
                ? <>Climate Change:<br /><span style={{ color: '#6ee7b7' }}>The Scientific Reality</span></>
                : <>İklim Değişikliği:<br /><span style={{ color: '#6ee7b7' }}>Bilimsel Gerçekler</span></>
              }
            </h2>

            <p className="text-white/75 text-sm md:text-base leading-relaxed max-w-2xl">
              {isEn
                ? 'Global scientific data shows the climate crisis is accelerating. As Hacettepe University Faculty of Science, we contribute to climate science, environmental research and sustainability through our academic work.'
                : 'Küresel bilimsel veriler, iklim krizinin hızlandığını gösteriyor. Hacettepe Üniversitesi Fen Fakültesi olarak; iklim bilimi, çevre araştırmaları ve sürdürülebilirlik alanlarına akademik çalışmalarımızla katkı sunuyoruz.'
              }
            </p>
          </div>
        </motion.div>

        {/* ══════════ İstatistik kartları ══════════ */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-14"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          {stats.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="relative rounded-2xl p-5 bg-white" style={{ border: '1px solid #e8f2ec', boxShadow: '0 10px 28px rgba(15,60,45,0.06)' }}>
                <div className="flex items-center justify-center w-10 h-10 rounded-xl mb-4" style={{ background: `${s.color}15` }}>
                  <Icon className="w-5 h-5" style={{ color: s.color }} />
                </div>
                <div className="font-black text-2xl md:text-[1.7rem] leading-none mb-2 tabular-nums" style={{ color: s.color }}>{s.value}</div>
                <div className="text-gray-900 text-xs font-bold mb-1.5 leading-snug">{s.label}</div>
                <div className="text-gray-400 text-[11px] leading-relaxed">{s.desc}</div>
              </div>
            );
          })}
        </motion.div>

        {/* ══════════ Sorun / Çözüm ══════════ */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-16"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="relative rounded-3xl overflow-hidden" style={{ minHeight: '260px', boxShadow: '0 20px 50px rgba(0,0,0,0.14)' }}>
            <img src={IMG_PROBLEM} alt={isEn ? 'Industrial emissions' : 'Endüstriyel emisyon ve hava kirliliği'} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(30,10,5,0.15) 0%, rgba(20,8,4,0.92) 100%)' }} />
            <div className="relative z-10 h-full flex flex-col justify-end p-6" style={{ minHeight: '260px' }}>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] mb-2" style={{ color: '#fca5a5' }}>
                {isEn ? 'The Problem' : 'Sorun'}
              </span>
              <h3 className="text-white font-black text-lg mb-1.5">
                {isEn ? 'Greenhouse Gas Emissions' : 'Sera Gazı Emisyonları'}
              </h3>
              <p className="text-white/60 text-xs leading-relaxed max-w-sm">
                {isEn
                  ? 'Fossil fuel use and industrial activity keep driving atmospheric CO₂ levels upward.'
                  : 'Fosil yakıt kullanımı ve endüstriyel faaliyetler, atmosferdeki CO₂ seviyesini artırmaya devam ediyor.'
                }
              </p>
            </div>
          </div>

          <div className="relative rounded-3xl overflow-hidden" style={{ minHeight: '260px', boxShadow: '0 20px 50px rgba(0,0,0,0.14)' }}>
            <img src={IMG_SOLUTION} alt={isEn ? 'Solar panels' : 'Güneş enerjisi panelleri'} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(4,20,16,0.1) 0%, rgba(3,18,15,0.92) 100%)' }} />
            <div className="relative z-10 h-full flex flex-col justify-end p-6" style={{ minHeight: '260px' }}>
              <span className="text-[10px] font-black uppercase tracking-[0.2em] mb-2" style={{ color: '#6ee7b7' }}>
                {isEn ? 'The Solution' : 'Çözüm'}
              </span>
              <h3 className="text-white font-black text-lg mb-1.5">
                {isEn ? 'Renewable Energy Transition' : 'Yenilenebilir Enerji Dönüşümü'}
              </h3>
              <p className="text-white/60 text-xs leading-relaxed max-w-sm">
                {isEn
                  ? 'Clean energy, scientific research and global cooperation are our strongest tools against the climate crisis.'
                  : 'Temiz enerji, bilimsel araştırma ve küresel iş birliği; iklim krizine karşı en güçlü araçlarımız.'
                }
              </p>
            </div>
          </div>
        </motion.div>

        {/* ══════════ Fen Fakültesi'nden İklim Araştırmaları ══════════ */}
        <motion.div
          className="mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.24em] px-4 py-2 rounded-full mb-4" style={{
              background: 'rgba(15,118,110,0.08)',
              color: '#0f766e',
            }}>
              <Microscope className="w-3 h-3" />
              {isEn ? 'Department of Biology Research' : 'Biyoloji Bölümü Araştırmaları'}
            </span>
            <h2 className="font-black text-gray-900 leading-tight mb-3" style={{ fontSize: 'clamp(1.6rem, 3vw, 2.3rem)' }}>
              {isEn
                ? <>Climate Research at Our<br /><span style={{ color: '#0f766e' }}>Faculty of Science</span></>
                : <>Fen Fakültesi'nden<br /><span style={{ color: '#0f766e' }}>İklim Araştırmaları</span></>
              }
            </h2>
            <p className="text-gray-500 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              {isEn
                ? 'Selected climate-change-focused research projects, international publications and academic work carried out within the Hacettepe University Faculty of Science Department of Biology:'
                : 'Hacettepe Üniversitesi Fen Fakültesi Biyoloji Bölümü bünyesinde yürütülen iklim değişikliği odaklı araştırma projeleri, uluslararası yayınlar ve akademik çalışmalardan öne çıkanlar:'
              }
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {researchAreas.map((area, i) => {
              const Icon = area.icon;
              const isLast = i === researchAreas.length - 1;
              return (
                <motion.div
                  key={i}
                  className={`relative rounded-3xl bg-white p-6 md:p-7 ${isLast ? 'lg:col-span-2' : ''}`}
                  style={{ border: '1px solid #eef4f0', boxShadow: '0 12px 32px rgba(15,60,45,0.06)' }}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                >
                  <div className="flex items-start gap-4 mb-5">
                    <div className="flex items-center justify-center w-12 h-12 rounded-2xl flex-shrink-0" style={{
                      background: `linear-gradient(135deg, ${area.color}, ${area.color}cc)`,
                      boxShadow: `0 8px 20px ${area.color}35`,
                    }}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="font-black text-gray-900 text-base md:text-lg leading-snug pt-1.5">
                      {isEn ? area.title.en : area.title.tr}
                    </h3>
                  </div>

                  <div className={isLast ? 'grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5' : 'space-y-5'}>
                    {area.items.map((item, j) => (
                      <div key={j} className="pl-4" style={{ borderLeft: `2px solid ${area.color}30` }}>
                        <p className="text-gray-900 text-[13px] font-bold leading-snug">
                          {item.name}
                          {item.lab && <span className="text-gray-400 font-semibold"> · {item.lab}</span>}
                        </p>
                        <p className="text-gray-500 text-[12.5px] leading-relaxed mt-1.5">
                          {item.desc}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* ══════════ Geçiş: Bilimden Küresel Eyleme ══════════ */}
        <motion.div
          className="flex flex-col items-center text-center mb-14"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-[11px] font-black uppercase tracking-[0.22em] text-gray-400 mb-2">
            {isEn ? 'From Science to Global Action' : 'Bilimden Küresel Eyleme'}
          </span>
          <ArrowDown className="w-4 h-4 text-gray-300" />
        </motion.div>

        {/* ══════════ COP31 ══════════ */}
        <motion.div
          className="relative rounded-3xl overflow-hidden mb-10 bg-white flex flex-col lg:flex-row lg:items-stretch"
          style={{ boxShadow: '0 24px 60px rgba(3,40,50,0.18), 0 0 0 1px rgba(3,40,50,0.05)' }}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Görsel */}
          <div className="relative lg:w-[42%] flex-shrink-0" style={{ minHeight: '280px' }}>
            <img src={IMG_COP31} alt={isEn ? 'Earth from space' : 'Uzaydan dünya'} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(200deg, rgba(3,20,30,0.15) 0%, rgba(3,20,30,0.55) 100%)' }} />
          </div>

          {/* Bilgi paneli */}
          <div className="relative flex-1 flex flex-col justify-center gap-5 p-7 md:p-10" style={{ background: 'linear-gradient(120deg, #064e3b 0%, #0f766e 55%, #0c4a6e 100%)' }}>
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 65%)' }} />

            <div className="relative">
              <span className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.24em] text-white/70 mb-3">
                <Leaf className="w-3.5 h-3.5" />
                {isEn ? 'COP31 · UN Climate Change Conference' : 'COP31 · BM İklim Değişikliği Konferansı'}
              </span>
              <h2 className="font-black text-white leading-tight mb-3" style={{ fontSize: 'clamp(1.5rem, 2.6vw, 2.1rem)' }}>
                {isEn
                  ? <>COP31 is Coming to<br /><span style={{ color: '#6ee7b7' }}>Antalya, Türkiye</span></>
                  : <>COP31, Antalya'da<br /><span style={{ color: '#6ee7b7' }}>Türkiye'de Buluşuyor</span></>
                }
              </h2>
              <p className="text-white/70 text-sm leading-relaxed max-w-lg">
                {isEn
                  ? 'The 31st session of the UN Climate Change Conference (COP31) will be hosted by Türkiye in Antalya, bringing together nearly 100,000 participants to accelerate the implementation of the Paris Agreement and scale up global climate finance and action.'
                  : 'Birleşmiş Milletler İklim Değişikliği Çerçeve Sözleşmesi Taraflar Konferansı\'nın (COP) 31. oturumuna Türkiye, Antalya\'da ev sahipliği yapacak. Yaklaşık 100 bin katılımcıyı bir araya getirecek zirve; Paris Anlaşması\'nın uygulanmasını hızlandırmayı ve küresel iklim finansmanı ile eylemini ölçeklendirmeyi hedefliyor.'
                }
              </p>
            </div>

            {/* Detay kutuları */}
            <div className="relative flex flex-col sm:flex-row gap-3">
              <div className="flex items-center gap-2.5 bg-white/10 border border-white/15 backdrop-blur-sm rounded-2xl px-4 py-3">
                <Calendar className="w-4 h-4 text-white/70 flex-shrink-0" />
                <span className="text-white text-sm font-semibold">{isEn ? 'Nov 9–20, 2026' : '9-20 Kasım 2026'}</span>
              </div>
              <div className="flex items-center gap-2.5 bg-white/10 border border-white/15 backdrop-blur-sm rounded-2xl px-4 py-3">
                <MapPin className="w-4 h-4 text-white/70 flex-shrink-0" />
                <span className="text-white text-sm font-semibold">{isEn ? 'Antalya, Türkiye' : 'Antalya, Türkiye'}</span>
              </div>
              <div className="flex items-center gap-2.5 bg-white/10 border border-white/15 backdrop-blur-sm rounded-2xl px-4 py-3">
                <Users className="w-4 h-4 text-white/70 flex-shrink-0" />
                <span className="text-white text-sm font-semibold">{isEn ? '~100,000 Participants' : '~100.000 Katılımcı'}</span>
              </div>
            </div>

            <a
              href="https://cop31.tr/tr"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative inline-flex items-center justify-between gap-3 w-full sm:w-auto px-6 py-3.5 rounded-2xl font-bold text-sm text-white mt-1"
              style={{
                background: 'linear-gradient(90deg, #059669, #0d9488)',
                boxShadow: '0 10px 28px rgba(5,150,105,0.35), inset 0 1px 0 rgba(255,255,255,0.2)',
              }}
            >
              <span className="flex items-center gap-2.5">
                <ExternalLink className="w-4 h-4" />
                {isEn ? 'Learn More About COP31' : 'COP31 Hakkında Detaylı Bilgi'}
              </span>
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </motion.div>

        {/* ══════════ 10 Öncelikli Eylem Teması ══════════ */}
        <motion.div
          className="relative"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
        >
          {/* Yavaşça dönen dekoratif konik gradyan */}
          <motion.div
            className="absolute left-1/2 top-1/2 pointer-events-none -z-10"
            style={{
              width: '640px',
              height: '640px',
              marginLeft: '-320px',
              marginTop: '-320px',
              background: 'conic-gradient(from 0deg, rgba(5,150,105,0.08), rgba(2,132,199,0.08), rgba(217,119,6,0.08), rgba(124,58,237,0.08), rgba(5,150,105,0.08))',
              borderRadius: '9999px',
              filter: 'blur(40px)',
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 40, repeat: Infinity, ease: 'linear' }}
          />

          <div className="text-center mb-8">
            <span className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.24em] px-4 py-2 rounded-full" style={{
              background: 'rgba(15,118,110,0.08)',
              color: '#0f766e',
            }}>
              <Leaf className="w-3 h-3" />
              {isEn ? 'COP31 Priority Action Themes' : 'COP31 Öncelikli Eylem Temaları'}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
            {themes.map((theme, i) => {
              const Icon = theme.icon;
              return (
                <motion.div
                  key={i}
                  className="group relative flex flex-col items-center text-center gap-3 rounded-2xl p-5 bg-white overflow-hidden cursor-default"
                  style={{ border: '1px solid #eef4f0' }}
                  initial={{ opacity: 0, y: 18, scale: 0.94 }}
                  whileInView={{ opacity: 1, y: 0, scale: 1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -6, boxShadow: `0 18px 36px ${theme.color}30` }}
                  transition={{ duration: 0.5, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                >
                  {/* Hover'da beliren renkli parıltı */}
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                    style={{ background: `radial-gradient(120px circle at 50% 0%, ${theme.color}18, transparent 70%)` }}
                  />

                  {/* İkon + nabız halkası */}
                  <div className="relative flex items-center justify-center w-12 h-12 flex-shrink-0">
                    <motion.span
                      className="absolute inset-0 rounded-full"
                      style={{ background: `${theme.color}22` }}
                      animate={{ scale: [1, 1.35, 1], opacity: [0.6, 0, 0.6] }}
                      transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut', delay: i * 0.15 }}
                    />
                    <motion.div
                      className="relative flex items-center justify-center w-11 h-11 rounded-xl"
                      style={{ background: `linear-gradient(135deg, ${theme.color}, ${theme.color}cc)`, boxShadow: `0 6px 16px ${theme.color}40` }}
                      animate={{ y: [0, -3, 0] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: i * 0.2 }}
                    >
                      <Icon className="w-5 h-5 text-white" />
                    </motion.div>
                  </div>

                  <span className="relative text-gray-700 text-[11.5px] font-bold leading-tight group-hover:text-gray-900 transition-colors">
                    {isEn ? theme.en : theme.tr}
                  </span>

                  <span className="absolute bottom-0 left-0 right-0 h-[3px] scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" style={{ background: theme.color }} />
                </motion.div>
              );
            })}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default ClimateSection;
